/**
 * SectionConfig — right panel for editing a selected section's settings.
 * Shows different fields depending on section.type.
 */
import React from 'react';
import {
  Box, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Switch, TextField, Typography
} from '@mui/material';
import TokenPicker from './TokenPicker';

export default function SectionConfig({ section, tokenCatalog = [], onChange }) {
  if (!section) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a section to configure it.
        </Typography>
      </Box>
    );
  }

  const update = (field, value) => onChange({ ...section, [field]: value });

  const handleTokenInsert = (token) => {
    const currentContent = section.content || '';
    update('content', currentContent + token);
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2">
        Configure: {section.title || section.type}
      </Typography>

      {/* Shared fields */}
      <TextField
        label="Section Title"
        size="small"
        fullWidth
        value={section.title || ''}
        onChange={(e) => update('title', e.target.value)}
        helperText="Display label (used only in editor)"
      />

      {/* Content / text field for notes, headers, footers */}
      {['notes', 'header', 'footer'].includes(section.type) && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TokenPicker catalog={tokenCatalog} onInsert={handleTokenInsert} />
          </Box>
          <TextField
            label="Content"
            size="small"
            fullWidth
            multiline
            rows={5}
            value={section.content || ''}
            onChange={(e) => update('content', e.target.value)}
            helperText="Use {{token.key}} to insert dynamic values"
          />
        </>
      )}

      {/* Alignment (for header/footer/customer) */}
      {['header', 'footer', 'customer', 'totals'].includes(section.type) && (
        <FormControl size="small" fullWidth>
          <InputLabel>Alignment</InputLabel>
          <Select
            label="Alignment"
            value={section.align || 'left'}
            onChange={(e) => update('align', e.target.value)}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Show logo (header only) */}
      {section.type === 'header' && (
        <FormControlLabel
          control={<Switch checked={!!section.showLogo} onChange={(e) => update('showLogo', e.target.checked)} />}
          label="Show practice logo"
        />
      )}

      {/* Spacer height */}
      {section.type === 'spacer' && (
        <TextField
          label="Height (px)"
          size="small"
          type="number"
          value={section.height || 20}
          onChange={(e) => update('height', parseInt(e.target.value, 10) || 20)}
        />
      )}

      {/* Items section column visibility */}
      {section.type === 'items' && (
        <>
          <Typography variant="caption" color="text.secondary">Visible columns</Typography>
          {['description', 'quantity', 'unitPrice', 'discount', 'total'].map((col) => (
            <FormControlLabel
              key={col}
              control={
                <Switch
                  size="small"
                  checked={section.columns ? section.columns.includes(col) : true}
                  onChange={(e) => {
                    const cols = section.columns || ['description', 'quantity', 'unitPrice', 'discount', 'total'];
                    const next = e.target.checked ? [...new Set([...cols, col])] : cols.filter(c => c !== col);
                    update('columns', next);
                  }}
                />
              }
              label={col}
            />
          ))}
        </>
      )}
    </Box>
  );
}
